
import { Component, Vue, Prop } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import { get } from "lodash"

import "@/scripts/array-operations"
import { caseColumns, CaseColumn } from "@/types/caseColumns"

import { Case, Field, DataType, CaseDetails, dataTypeByFieldType, CalculationDataTypeValue } from "@/types"
import { Team } from "@/types/team"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import moment from "moment"

import SkeletonLoader from "../SkeletonLoader.vue"
import CaseTable from "@/components/CaseList/CaseTable.vue"

import { View } from "@/types/views/view"
import { ViewSummary } from "@/types/views/viewSummary"
import { Severity } from "@/types/severity"
import { groupingFunctions } from "@/types/dataType"

import { formatDateMixin } from "@/mixins/formatDateMixin"
import { CalculationType } from "@/types/Field"

import UserImage from "@/components/UserImage.vue"
import { CaseUser, UserRole } from "@/types/caseUser"
import CaseUsers from "../CaseDetails/CaseUsers.vue"
import { UserSettings } from "@/types/userSettings"
import { Schedule } from "@/types/schedule"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"case-table": CaseTable,
		"user-image": UserImage,
		"schedule-popup": () => import("@/components/Config/Schedules/ScheduleInstance.vue")
	}
})
export default class CaseList extends Vue {
	@Prop({ type: String, default: "" })
	filter!: string;

	@Prop()
	cases!: Case[] | null;

	@Prop()
	activeView!: View | null;

	@Prop({ type: Boolean, default: true })
	enableFiltering!: boolean;

	@Prop({ type: Number, default: 50 })
	perPage!: number;

	@caseStore.State severities!: Severity[];

	@Getter("getScreenWidth")
	public screenWidth!: number;

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Getter("getUserSettings")
	public userSettings!: UserSettings;

	@Getter("getUseV2")
	private readonly shouldUseNewInterface!: boolean;

	@setupStore.Getter("getFilterableFields")
	getFilterableFields!: CaseColumn[];

	@setupStore.State fields!: Field[];
	@setupStore.Action loadFieldsIfUnloaded!: () => Promise<void>;

	public fieldsDictionary: any = [];

	private searchFilter: string = "";
	private casesFiltered: CaseDetails[] = [];
	private viewScheduleInstance: {
		schedule: Schedule,
		date: Date
	} | null = null

	private groupPageNumbers: { [key: string]: number } = {}

	async mounted () {
		await this.loadFieldsIfUnloaded()
	}

	public get columns (): any[] {
		if (!this.activeView) {
			return [
				{
					key: "caseNumber",
					label: "Case Number"
				},
				{
					key: "title",
					label: "Title"
				},
				{
					key: "templateTitle",
					label: "Template"
				},
				{
					key: "assigneeId",
					label: "Assignee",
					dataType: "user"
				}
			]
		}

		return this.activeView.columns.map(c => {
			if (c.startsWith("fields.")) {
				let fieldId = c.substring(7)

				if (fieldId.endsWith(".label")) {
					fieldId = fieldId.substring(0, fieldId.length - 6)
				}

				const fieldColumn = this.fields.find(field => field.id === fieldId)
				if (fieldColumn) {
					return {
						key: c,
						fieldId: fieldColumn.id,
						label: fieldColumn.title,
						dataType: fieldColumn.dataType,
						fieldDataType: fieldColumn.dataType === "calculated"
							? CalculationDataTypeValue[fieldColumn.extensions!.calculationType as CalculationType]
							: dataTypeByFieldType[fieldColumn.dataType!]
								? dataTypeByFieldType[fieldColumn.dataType!]
								: null,
						extensions: fieldColumn.extensions,
						dynamicField: true,
						listLabel: "label"
					}
				}
			} else {
				const caseColumn = this.getFilterableFields.find(column => column.key === c)
				if (caseColumn) {
					return {
						key: caseColumn.key,
						displayKey: caseColumn.displayKey,
						label: caseColumn.label,
						dataType: caseColumn.dataType,
						fieldDataType: caseColumn.fieldDataType,
						rounding: caseColumn.rounding,
						listLabel: caseColumn.listLabel || "title",
						listItemType: caseColumn.listItemType
					}
				}
			}

			return null
		}).filter(c => !!c)
	}

	public get groupedCases (): any[] {
		// Build fields dictionary for quick access using case columns and custom fields
		this.getFilterableFields.forEach((field: CaseColumn) => {
			this.fieldsDictionary[field.key] = { title: field.label, dataType: field.dataType, displayKey: field.displayKey, group: field.group }
		})
		this.fields.forEach((field: any) => {
			this.fieldsDictionary["fields." + field.id] = {
				title: field.title,
				dataType: field.dataType,
				displayKey: field.dataType === "user-role" ? null : "fields." + field.id + ".label",
				fieldDataType: field.dataType === "calculated"
					? CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
					: dataTypeByFieldType[field.dataType!]
						? dataTypeByFieldType[field.dataType!]
						: null
			}
		})

		if (this.cases == null || this.cases.length === 0) {
			return []
		}

		let cases = this.cases

		if (this.enableFiltering && !!this.searchFilter && /\S/.test(this.searchFilter)) {
			this.casesFiltered = []
			this.columns.forEach((viewColumn: CaseColumn) => {
				cases.forEach((existingCase: any) => {
					if (this.casesFiltered.some(filteredCase => filteredCase.id === existingCase.id) === false) {
						let caseColumnValue = this.getCaseColumnValue(viewColumn, existingCase)
						if (caseColumnValue) {
							if (caseColumnValue.toLocaleLowerCase().includes(this.searchFilter.toLocaleLowerCase().trim())) {
								this.casesFiltered.push(existingCase)
							}
						}
					}
				})
			})

			cases = this.casesFiltered
		}

		if (this.activeView && this.activeView.sortBy) {
			// Sorting by severity should be done by level not title
			if (this.activeView!.sortBy === "severityTitle") {
				cases.forEach(c => {
					c.severityLevel = this.severityLevel(c.severityTitle)
				})
				cases = cases.sortBy("severityLevel", false)
			} else {
				cases = cases.sortBy(this.activeView!.sortBy as any, false)
			}
		} else {
			cases = cases.sortBy("created", false)
		}

		if (this.activeView && this.activeView.groupBy) {
			if (!this.fieldsDictionary[this.activeView!.groupBy]) {
				this.activeView.groupBy = null
			}
		}

		if (this.activeView && this.activeView.groupBy) {
			const groupByKeyColumn = this.fieldsDictionary[this.activeView!.groupBy]
			const groupBy = groupByKeyColumn
				? groupByKeyColumn.displayKey
				: this.activeView!.groupBy

			const caseUsers: { [key:string]: any } = {}
			let getGroups: (record: any, groupByValue: any, cache: { [key:string]: any }, groupByOption?: string | null) => { key: string, label: string, value: any }[] =
				groupByKeyColumn && groupByKeyColumn.group
					? groupByKeyColumn.group
					: groupByKeyColumn && groupingFunctions.hasOwnProperty(groupByKeyColumn.fieldDataType)
						? get(groupingFunctions, groupByKeyColumn.fieldDataType)
						: (record: any, groupByValue: any, cache, groupByOption: string) => {
							return [
								{
									key: groupByValue || "not set",
									label: groupByValue || "Not Set",
									value: groupByValue
								}
							]
						}

			const groupLabels: { [key:string]: {
					label: string,
					value: any,
					sortValue?: any
				}
			} = {}
			const caseGroups = cases
				.reduce((group: any, caseItem: any) => {
					let groupKey = get(caseItem, groupBy)
					if (!groupKey) {
						groupKey = get(caseItem, this.activeView!.groupBy!)
						if (groupKey && groupKey.value !== undefined) {
							groupKey = groupKey.value
						}
					}

					if (this.activeView && this.activeView.groupByDataType === DataType.dateTime) {
						const value = groupKey
						groupKey = groupKey
							? moment(groupKey).format("L")
							: "Not set"
						groupLabels[groupKey] = {
							label: groupKey,
							value,
							sortValue: value
						};
						(group[groupKey] = group[groupKey] || []).push(caseItem)
					} else {
						const itemGroups = getGroups(caseItem, groupKey, caseUsers, this.activeView!.groupByOption)
						itemGroups.forEach(itemGroup => {
							groupLabels[itemGroup.key] = {
								label: itemGroup.label,
								value: itemGroup.value
							};
							(group[itemGroup.key] = group[itemGroup.key] || []).push(caseItem)
						})
					}

					return group
				}, {})
			return Object.keys(caseGroups).map(groupKey => {
				return Vue.observable({
					key: groupKey,
					label: groupLabels[groupKey] ? groupLabels[groupKey].label : groupKey,
					value: groupLabels[groupKey] ? groupLabels[groupKey].value : groupKey,
					sort: this.activeView!.groupBy === "severityId"
						? this.severityLevel(groupKey)
						: groupLabels[groupKey]
							? groupLabels[groupKey].sortValue
								? groupLabels[groupKey].sortValue
								: groupLabels[groupKey].label
							: groupKey,
					cases: caseGroups[groupKey],
					currentPage: this.groupPageNumbers[groupKey] ?? 1
				})
			}).sortBy("sort")
		} else {
			return [Vue.observable({
				label: null,
				cases: cases,
				currentPage: this.groupPageNumbers["{(( _UNGROUPED_ ))}"] ?? 1
			})]
		}
	}

	private severityLevel (severityTitle: string): number {
		const severity = this.severities && this.severities.find(p => p.title === severityTitle)
		if (severity) {
			return 9999 - severity.level
		} else {
			return 0
		}
	}

	// Method to display checkbox value as string dependent on its value
	private displayCheckBoxValue (value: string): string {
		switch (value) {
			case "null" || "false":
				return "No"
			case "undefined":
				return "Not Specified"
			default:
				return "Yes"
		}
	}

	private assigneeUserName (caseDetails: CaseDetails, id: string): string {
		if (!caseDetails.assigneeId) return "Unassigned"
		const user = caseDetails.users.find(e => e.id === id)

		if (!user) return "Unknown user"
		return user.name
	}

	private newCase (): void {
		this.$router.push({ path: "/cases/New" })
	}

	private getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	private getCaseColumnValue (viewColumn: CaseColumn, existingCase: any): string | null {
		let caseColumnValue: string | null = null

		let columnKey: string = ""
		let columnValid: boolean = this.getFilterableFields.some(caseColumn => {
			if (caseColumn.key === viewColumn.key) {
				columnKey = viewColumn.key
			} else if (caseColumn.displayKey && caseColumn.displayKey === viewColumn.key) {
				columnKey = caseColumn.displayKey
			}

			if (existingCase[columnKey]) {
				return true
			}

			return false
		})

		if (columnValid === false) {
			return null
		}

		if (viewColumn.dataType === "date") {
			caseColumnValue = formatDateMixin.methods.displayDateOnlyFromISOString(viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey])
		} else if (viewColumn.dataType === "date-time") {
			caseColumnValue = formatDateMixin.methods.displayDateFromISOString(viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey], this.$store)
		} else if (viewColumn.dataType === "list" || viewColumn.dataType === "list-multiple") {
			caseColumnValue = existingCase[columnKey].label ? existingCase[columnKey].label
				: viewColumn.displayKey ? existingCase[columnKey]
					: viewColumn.fieldId ? this.getFieldLabel(viewColumn.fieldId, existingCase)
						: (existingCase[columnKey] && Array.isArray(existingCase[columnKey])) ? existingCase[columnKey].map((i: { [x: string]: any; }) => i[viewColumn.listLabel || "title"]).join(", ")
							: viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey]
		} else if (viewColumn.dataType === "dollar" && existingCase[columnKey]) {
			caseColumnValue = viewColumn.dynamicField ? existingCase[columnKey] : existingCase[columnKey]
		} else if (viewColumn.dataType === "checkbox" && existingCase[columnKey]) {
			caseColumnValue = !(viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey]) ? "No" : "Yes"
		} else if (viewColumn.dataType === "calculated") {
			caseColumnValue = viewColumn.dynamicField ? existingCase[columnKey] : existingCase[columnKey]
		} else if (viewColumn.dataType === "list-data") {
			caseColumnValue = existingCase[columnKey].label
		} else if (viewColumn.dataType === "user" && existingCase[columnKey] && existingCase.users) {
			caseColumnValue = existingCase.users.find((u: { id: any; }) => u.id === existingCase[columnKey]).name
		} else if (viewColumn.dataType === "user-name") {
			caseColumnValue = existingCase[columnKey]
		} else if (!viewColumn.dataType || viewColumn.dataType === "text") {
			caseColumnValue = viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey]
		}

		return caseColumnValue
	}

	private get caseFilteringTotal (): string {
		if (this.cases && this.searchFilter && /\S/.test(this.searchFilter)) {
			return `Showing ${this.casesFiltered.length} of ${this.cases.length} records`
		} else if (this.cases) {
			return `${this.cases.length} records`
		} else {
			return ""
		}
	}

	forecastClicked (caseItem: { scheduling: any }) {
		console.log("CaseList", "forecastClicked")
		if (!caseItem.scheduling) return

		this.viewScheduleInstance = {
			schedule: {
				id: caseItem.scheduling.scheduleId,
				title: caseItem.scheduling.scheduleTitle
			} as Schedule,
			date: new Date(caseItem.scheduling.instanceDate)
		}

		console.log("CaseList", this.viewScheduleInstance)
	}

	scheduleUpdated (schedule: Schedule) {
	}

	private currentPageChanged (group: any, pageNumber: number) {
		switch (group.key) {
			case undefined:
			case null:
				this.groupPageNumbers["{(( _UNGROUPED_ ))}"] = pageNumber
				break
			default:
				this.groupPageNumbers[group.key] = pageNumber
		}

		const groupIndex = this.groupedCases.indexOf(group)

		group.currentPage = pageNumber

		if (groupIndex >= 0) {
			Vue.set(this.groupedCases[groupIndex], "currentPage", pageNumber)
		}
	}
}
